import { useState, useEffect } from 'react'
import { Plus } from 'react-feather'
import { connect } from 'react-redux'
import { Row, Col, Form, FormGroup, Label, Input } from 'reactstrap'
import { supportedCountries } from '../../configs/supportedCountries'
import { validateField, createLabels } from '../../util/forms'
import { validateAddressLines } from '../../util/utils'
import CountrySelect from '../addresses/CountrySelect'
import StateProvinceSelect from '../addresses/StateProvinceSelect'

const BillingAddressForm = ({ address, onAddressChange, portal }) => {
  const [showAddress2, setShowAddress2] = useState(Boolean(address?.address_line_2))
  const [formErrors, setFormErrors] = useState({
    full_name: null,
    address_line_1: null,
    address_line_2: null,
    company_name: null,
    city: null,
    state: null,
    zip_code: null,
    country: null
  })

  // Update showAddress2 when address changes
  useEffect(() => {
    if (address?.address_line_2) {
      setShowAddress2(true)
    }
  }, [address?.address_line_2])

  // Required field indicator
  const requiredAsterisk = <span className="text-danger">*</span>

  const countries = supportedCountries(portal.supported_countries)
  const selectedCountry = countries.find(c => c.country_code === (address?.country_code || 'US')) || {
    labels: {
      postcode: 'ZIP Code',
      region: 'State',
      city: 'City'
    }
  }

  const labels = {
    ...createLabels(selectedCountry),
    address_line_1: 'Street Address',
    state: selectedCountry.labels.region,
    country: 'Country'
  }

  const handleChange = e => {
    const { name, value } = e.target

    if (name === 'address_line_1' || name === 'address_line_2') {
      onAddressChange({
        ...address,
        [name]: value
      })

      // Validate address line length
      const newErrors = validateAddressLines(
        name,
        name === 'address_line_1' ? value : address?.address_line_1,
        name === 'address_line_2' ? value : address?.address_line_2,
        formErrors
      )

      // Also check if address_line_1 is required
      if (name === 'address_line_1' && !value?.trim()) {
        newErrors.address_line_1 = 'Street Address is required'
      }

      setFormErrors(newErrors)
      return
    }

    const label = labels[name] || name
    const error = validateField(name, value, portal.international_shipping, label)
    setFormErrors(prev => ({ ...prev, [name]: error }))
    onAddressChange({
      ...address,
      [name]: value
    })
  }

  const handleChangeCountry = countryObject => {
    setFormErrors(prev => ({ ...prev, city: null, zip_code: null, state: null, country: null }))
    const updated = {
      ...address,
      country: countryObject.name,
      country_code: countryObject.country_code,
      state: ''
    }
    onAddressChange(updated, { commit: true })
  }

  const handleChangeState = stateProvince => {
    setFormErrors(prev => ({
      ...prev,
      state: stateProvince.value ? null : `${selectedCountry.labels.region} is required`
    }))
    const updated = { ...address, state: stateProvince.value }
    onAddressChange(updated, { commit: true })
  }

  const toggleAddress2 = () => setShowAddress2(!showAddress2)

  return (
    <Form>
      {/* Full Name */}
      <FormGroup className="mb-3">
        <Label>Full Name {requiredAsterisk}</Label>
        <Input
          type="text"
          name="full_name"
          value={address?.full_name}
          onChange={handleChange}
          onBlur={() => onAddressChange(address, { commit: true })}
          placeholder="Enter full name"
          invalid={!!formErrors.full_name}
        />
        {formErrors.full_name && <span className="text-danger small">{formErrors.full_name}</span>}
      </FormGroup>

      {/* Country Select */}
      <FormGroup className="mb-3">
        <Label>Country {requiredAsterisk}</Label>
        <CountrySelect
          handleChange={handleChangeCountry}
          countryCode={address?.country_code}
        />
        {formErrors.country && <span className="text-danger small">{formErrors.country}</span>}
      </FormGroup>

      {/* Company (Optional) */}
      <FormGroup className="mb-3">
        <Label>Company (Optional)</Label>
        <Input
          type="text"
          name="company_name"
          value={address?.company_name}
          onChange={handleChange}
          onBlur={() => onAddressChange(address, { commit: true })}
          placeholder="Enter company name"
          invalid={!!formErrors.company_name}
        />
        {formErrors.company_name && <span className="text-danger small">{formErrors.company_name}</span>}
      </FormGroup>

      {/* Street Address */}
      <FormGroup className="mb-3">
        <Label>Street Address {requiredAsterisk}</Label>
        <Input
          type="text"
          name="address_line_1"
          value={address?.address_line_1}
          onChange={handleChange}
          onBlur={() => onAddressChange(address, { commit: true })}
          placeholder="Enter street address"
          invalid={!!formErrors.address_line_1}
        />
        {formErrors.address_line_1 && <span className="text-danger small">{formErrors.address_line_1}</span>}
      </FormGroup>

      {/* Address Line 2 Toggle */}
      {!showAddress2 && (
        <FormGroup className="mb-3">
          <p onClick={toggleAddress2} style={{ cursor: 'pointer', marginBottom: 0, color: '#007bff' }}>
            <Plus size={16} />
            &nbsp;&nbsp;<span style={{ textDecoration: 'underline' }}>Add Address Line 2</span>
          </p>
        </FormGroup>
      )}

      {/* Address Line 2 */}
      {showAddress2 && (
        <FormGroup className="mb-3">
          <Label>Address Line 2</Label>
          <Input
            type="text"
            name="address_line_2"
            value={address?.address_line_2}
            onChange={handleChange}
            onBlur={() => onAddressChange(address, { commit: true })}
            placeholder="Apt, Suite, Unit, etc."
          />
        </FormGroup>
      )}

      {/* City, State, ZIP Row */}
      <Row>
        <Col md={4}>
          <FormGroup className="mb-3">
            <Label>{selectedCountry.labels.city} {requiredAsterisk}</Label>
            <Input
              type="text"
              name="city"
              value={address?.city}
              onChange={handleChange}
              onBlur={() => onAddressChange(address, { commit: true })}
              placeholder={selectedCountry.labels.city}
              invalid={!!formErrors.city}
            />
            {formErrors.city && <span className="text-danger small">{formErrors.city}</span>}
          </FormGroup>
        </Col>

        <Col md={4}>
          {selectedCountry.labels.region !== 'N/A' && (
            <FormGroup className="mb-3">
              <Label>{selectedCountry.labels.region} {requiredAsterisk}</Label>
              <StateProvinceSelect
                countryCode={address?.country_code}
                handleChangeState={handleChangeState}
                stateValue={address?.state}
              />
              {formErrors.state && <span className="text-danger small">{formErrors.state}</span>}
            </FormGroup>
          )}
        </Col>

        <Col md={4}>
          <FormGroup className="mb-3">
            <Label>{selectedCountry.labels.postcode} {requiredAsterisk}</Label>
            <Input
              type="text"
              name="zip_code"
              value={address?.zip_code}
              onChange={handleChange}
              onBlur={() => onAddressChange(address, { commit: true })}
              placeholder={selectedCountry.labels.postcode}
              invalid={!!formErrors.zip_code}
            />
            {formErrors.zip_code && <span className="text-danger small">{formErrors.zip_code}</span>}
          </FormGroup>
        </Col>
      </Row>
    </Form>
  )
}

const mapStateToProps = state => ({
  portal: state.portal
})

export default connect(mapStateToProps)(BillingAddressForm)
